import { ViewState } from './view';
import { PagesState, isPopupPanelID, tabFocusSelf, PopupInfo } from './pages';
import * as Action from '../actions/PageAction';
import { Dimension, Vector2D } from '../types/common';
import { Rectangle } from 'ark-common';
import { Publication, SpineItem } from '../types/epub';
import { getDefaultRendition } from '../utils/epub';
import { popupTitlebarHeight } from '../constants';
import { getMovableBounds, getWindowRect } from '../utils/view';

const resetTargetViewState = (view: ViewState, panelID: string): ViewState => {
  let updateNeeded = false;

  const newTabStates = { ...view.tabStates };
  if (newTabStates[panelID] !== undefined) {
    delete newTabStates[panelID];
    updateNeeded = true;
  }

  const newContentSizes = { ...view.frameContentSizes };
  if (newContentSizes[panelID] !== undefined) {
    delete newContentSizes[panelID];
    updateNeeded = true;
  }

  if (isPopupPanelID(panelID)) {
    const popupRects = { ...view.popupRects };
    delete popupRects[panelID];
    if (updateNeeded) {
      return {
        ...view,
        popupRects,
        tabStates: newTabStates,
        frameContentSizes: newContentSizes,
      };
    }
    return { ...view, popupRects };
  }

  if (updateNeeded) {
    return {
      ...view,
      tabStates: newTabStates,
      frameContentSizes: newContentSizes,
    };
  }

  return view;
};

const getDefaultSize = (item: SpineItem, action: Action.PageAction): Dimension => {
  let width: number = 800;
  let height: number = 300;

  if (item.fxl) {
    width = item.width;
    height = item.height;
  }
  if (action.type === Action.PAGE_OPEN_POPUP) {
    if (action.width !== undefined) {
      width = action.width;
    }
    if (action.height !== undefined) {
      height = action.height;
    }
  }

  return { width, height };
};

const getDefaultPosition = (
  containerRect: Rectangle,
  action: Action.PageAction,
  size: Dimension,
): Vector2D => {
  const { width, height } = size;

  let x;
  if (action.type === Action.PAGE_OPEN_POPUP) {
    x = action.x;
  }
  if (x === undefined) {
    x = (containerRect.width - width) / 2;
  }

  let y;
  if (action.type === Action.PAGE_OPEN_POPUP) {
    y = action.y;
  }
  if (y === undefined) {
    y = (containerRect.height - popupTitlebarHeight - height) / 2;
  }

  return { x, y };
};

const calcInitialPopupRect = (
  view: ViewState,
  item: SpineItem,
  action: Action.PageAction,
  popupInfo: PopupInfo,
): Rectangle => {
  const { constraint, topLevel, initialMargin } = popupInfo;
  const rootContainerRect = topLevel ? getWindowRect() : view.tabPanelAreaRect;

  const size = getDefaultSize(item, action);
  const { width: intrinsicWidth, height: intrinsicHeight } = size;

  const maxWidth = Math.max(
    constraint.minWidth,
    Math.min(rootContainerRect.width - (initialMargin * 2), constraint.maxWidth),
  );
  const maxHeight = Math.max(
    constraint.minHeight + popupTitlebarHeight,
    Math.min(
      rootContainerRect.height - (initialMargin * 2),
      constraint.maxHeight,
    ) - popupTitlebarHeight,
  );

  const horzScale = maxWidth / intrinsicWidth;
  const vertScale = maxHeight / intrinsicHeight;
  const scale = Math.min(horzScale, vertScale, 1);

  let width = intrinsicWidth * scale;
  let height = intrinsicHeight * scale;

  // タイトルバー無し(＝フローティングメニュー)以外はmin/maxの制限をかける
  if (action.type === Action.PAGE_OPEN_POPUP && action.titlebar) {
    width = Math.max(
      constraint.minWidth, Math.min(width, constraint.maxWidth),
    );
    height = Math.max(
      constraint.minHeight, Math.min(height, constraint.maxHeight),
    );
  }

  // openPopup()でサイズを指定されている場合はそれを優先する
  if (action.type === Action.PAGE_OPEN_POPUP && action.width !== undefined) {
    width = Math.max(
      action.minWidth || 0,
      Math.min(intrinsicWidth, (action.maxWidth || Number.MAX_VALUE)),
    );
  }
  if (action.type === Action.PAGE_OPEN_POPUP && action.height !== undefined) {
    height = Math.max(
      action.minHeight || 0,
      Math.min(intrinsicHeight, (action.maxHeight || Number.MAX_VALUE)),
    );
  }

  const rect = {
    ...getDefaultPosition(rootContainerRect, action, { width, height }),
    width,
    height,
  };

  // 操作不能にならないようにする
  const movableBounds = getMovableBounds(rect, rootContainerRect);

  return {
    ...rect,
    x: Math.min(movableBounds.right, Math.max(rect.x, movableBounds.left)),
    y: Math.min(movableBounds.bottom, Math.max(rect.y, movableBounds.top)),
  };
};

export const reduceViewByPageAction = (
  pages: PagesState,
  view: ViewState,
  publication: Publication,
  action: Readonly<Action.PageAction>,
): ViewState => {
  switch (action.type) {
    case Action.PAGE_OPEN_POPUP: {
      if (action.id in view.popupRects) {
        return view; // 既にポップアップが開いているので何も起きない
      }

      const item = getDefaultRendition(publication).itemIndices[action.path];
      if (item === undefined) {
        throw new Error('Popup item is not found.');
      }

      const popupRect = calcInitialPopupRect(view, item, action, pages.popups[action.id]);

      return {
        ...view,
        popupRects: {
          ...view.popupRects,
          [action.id]: popupRect,
        },
      };
    }
    case Action.PAGE_CLOSE_TAB:
      return resetTargetViewState(view, action.tabID);
    case Action.PAGE_CLOSE_POPUP:
      return resetTargetViewState(view, action.id);

    case Action.PAGE_REPLACE_CONTENT: {
      const targetTab = pages.tabs[pages.activeTab];
      if (targetTab.focus !== tabFocusSelf) {
        const item = getDefaultRendition(publication).itemIndices[action.path];
        if (item === undefined) {
          throw new Error('Popup item is not found.');
        }

        const popupRect = calcInitialPopupRect(view, item, action, pages.popups[targetTab.focus]);

        // popupの時はframeContentSizesを消した上で初期popupRectをセット
        return {
          ...resetTargetViewState(view, targetTab.focus),
          popupRects: {
            ...view.popupRects,
            [targetTab.focus]: popupRect,
          },
        };
      }
      return resetTargetViewState(view, targetTab.id);
    }

    case Action.PAGE_GO_PREV:
    case Action.PAGE_GO_NEXT: {
      const targetTab = pages.tabs[pages.activeTab];
      return resetTargetViewState(view, targetTab.id);
    }
    case Action.PAGE_GO_TO: {
      const targetTab = pages.tabs[action.tab];
      return resetTargetViewState(view, targetTab.id);
    }
    default:
      return view;
  }
};
